import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, fileSrc } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { StatusBadge, REQ_STATUS } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Inbox, Loader2, Check, X, CalendarClock, Phone, Mail, Car } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "-");
const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

export default function Requests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acceptFor, setAcceptFor] = useState(null);
  const [proposeFor, setProposeFor] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [proposedDate, setProposedDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/appointment-requests"); setRequests(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAccept = (r) => {
    setAcceptFor(r);
    const s = r.desired_date || new Date().toISOString();
    setStart(toLocalInput(s));
    setEnd(toLocalInput(new Date(new Date(s).getTime() + 8 * 3600000).toISOString()));
  };

  const doAccept = async () => {
    setBusy(true);
    try {
      await api.put(`/appointment-requests/${acceptFor.id}`, {
        action: "accept",
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
      });
      toast.success("Demande acceptée — rendez-vous ajouté au calendrier");
      setAcceptFor(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const doRefuse = async (r) => {
    try { await api.put(`/appointment-requests/${r.id}`, { action: "refuse" }); toast.success("Demande refusée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doPropose = async () => {
    setBusy(true);
    try {
      await api.put(`/appointment-requests/${proposeFor.id}`, { action: "propose", proposed_date: new Date(proposedDate).toISOString(), admin_note: note });
      toast.success("Nouvelle date proposée");
      setProposeFor(null); setNote("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const active = requests.filter((r) => ["recue", "date_proposee"].includes(r.status));
  const archived = requests.filter((r) => ["acceptee", "refusee"].includes(r.status));

  return (
    <div>
      <PageHeader title="Demandes de rendez-vous" subtitle={`${active.length} demande(s) à traiter`} icon={Inbox} />

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : requests.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
          Aucune demande. Partagez le lien public :{" "}
          <a href="/demande-rendez-vous" target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline">/demande-rendez-vous</a>
        </div>
      ) : (
        <div className="space-y-6">
          {[{ title: "À traiter", list: active }, { title: "Historique", list: archived }].map((sec) =>
            sec.list.length ? (
              <div key={sec.title}>
                <h3 className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{sec.title}</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {sec.list.map((r) => (
                    <div key={r.id} data-testid={`request-card-${r.id}`} className="bg-white border border-slate-200 rounded-xl p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="font-medium text-slate-900">{r.first_name} {r.last_name}</div>
                          <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                            {r.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{r.phone}</span>}
                            {r.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{r.email}</span>}
                          </div>
                        </div>
                        <StatusBadge status={r.status} map={REQ_STATUS} />
                      </div>

                      <div className="text-sm text-slate-700 flex items-center gap-1.5 mb-1"><Car className="w-4 h-4 text-emerald-600" />{[r.make, r.model, r.year].filter(Boolean).join(" ")} {r.plate && `· ${r.plate}`}</div>
                      {r.service && <div className="text-xs text-slate-500 mb-1">Prestation : {r.service}</div>}
                      {r.condition && <div className="text-xs text-slate-500 mb-1">État : {r.condition}</div>}
                      <div className="text-xs text-slate-500 flex items-center gap-1.5 mb-1"><CalendarClock className="w-3 h-3" />Souhaité : {fmtDate(r.desired_date)}{r.second_date && ` (ou ${fmtDate(r.second_date)})`}</div>
                      {r.comment && <div className="text-xs text-slate-500 italic mb-2">« {r.comment} »</div>}
                      {r.admin_note && <div className="text-xs text-cyan-600 mb-2">Note : {r.admin_note}</div>}

                      {r.photos?.length > 0 && (
                        <div className="flex gap-2 mb-3 flex-wrap">
                          {r.photos.map((p, i) => (
                            <a key={i} href={fileSrc(p.url || p.path)} target="_blank" rel="noreferrer" className="w-14 h-14 rounded-lg overflow-hidden border border-slate-200">
                              <img src={fileSrc(p.url || p.path)} alt="" className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      )}

                      {["recue", "date_proposee"].includes(r.status) && (
                        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
                          <Button size="sm" onClick={() => openAccept(r)} data-testid={`btn-accept-${r.id}`} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Check className="w-4 h-4 mr-1" /> Accepter</Button>
                          <Button size="sm" variant="outline" onClick={() => { setProposeFor(r); setProposedDate(toLocalInput(r.desired_date)); }} data-testid={`btn-propose-${r.id}`} className="border-cyan-500/30 bg-cyan-500/5 text-cyan-600 hover:bg-cyan-500/10"><CalendarClock className="w-4 h-4 mr-1" /> Proposer une date</Button>
                          <Button size="sm" variant="outline" onClick={() => doRefuse(r)} data-testid={`btn-refuse-${r.id}`} className="border-red-500/30 bg-red-500/5 text-red-600 hover:bg-red-500/10"><X className="w-4 h-4 mr-1" /> Refuser</Button>
                        </div>
                      )}
                      {r.status === "acceptee" && r.appointment_id && (
                        <Button size="sm" variant="outline" onClick={() => navigate("/calendar")} className="mt-2 border-slate-200 bg-slate-50">Voir dans le calendrier</Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null
          )}
        </div>
      )}

      {/* Accept dialog */}
      <Dialog open={!!acceptFor} onOpenChange={(o) => !o && setAcceptFor(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="text-slate-900">Accepter et planifier</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Un client et son véhicule seront créés automatiquement, et le rendez-vous ajouté au calendrier.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Début</Label><Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="input-accept-start" className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Fin</Label><Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="input-accept-end" className="bg-slate-50 border-slate-200" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAcceptFor(null)}>Annuler</Button>
            <Button onClick={doAccept} disabled={busy} data-testid="btn-confirm-accept" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Propose dialog */}
      <Dialog open={!!proposeFor} onOpenChange={(o) => !o && setProposeFor(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="text-slate-900">Proposer une autre date</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Date proposée</Label><Input type="datetime-local" value={proposedDate} onChange={(e) => setProposedDate(e.target.value)} data-testid="input-proposed-date" className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Message (facultatif)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} className="bg-slate-50 border-slate-200" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setProposeFor(null)}>Annuler</Button>
            <Button onClick={doPropose} disabled={busy} data-testid="btn-confirm-propose" className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Proposer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
